import { type WritableDraft } from 'immer';

import { type CanvasElement, type CanvasElementType } from '@blaze-runtime/engine-domain';
import { type RuntimeEventType } from '@blaze-shared/schema-domain';
import { type Workflow } from '@blaze-shared/workflow-domain';

import { type EngineState } from '../../+state/engine';
import type { ElementDefinitionsService } from '../../services/element-definitions.service';
import {
  createConnection,
  getUiRuntimeElement,
  getWorkflowInputName,
  getWorkflowRuntimeElement,
} from '../../utils/runtime';
import {
  type AiWorkflowFinalizationEntry,
  type ElementContext,
  type ResolvedLayoutInputCarrier,
  type WorkflowContext,
} from '../models/ai.model';

import { findElement } from './utils';
import { processWorkflowInputs } from './workflow-with-inputs';

export const SELECTOR_INPUT_TOKEN = '$selector';
export const SELECTED_INPUT_TOKEN = '$selected';

export interface ResolvedLayoutInput {
  name: string;
  eventType: RuntimeEventType;
}

export const DEFAULT_LAYOUT_INPUT_TOKENS: ReadonlyMap<string, ResolvedLayoutInput> = new Map([
  [SELECTED_INPUT_TOKEN, { name: 'selectedRow', eventType: 'row-select' }],
]);

export function wireLayoutWorkflowInputs<T extends CanvasElementType>(
  draft: WritableDraft<EngineState>,
  context: ElementContext<T>,
  workflow: Workflow,
  elementDefinitions: ElementDefinitionsService,
  tokenOverrides?: ReadonlyMap<string, ResolvedLayoutInput>,
  resolvedLayoutInputs?: ResolvedLayoutInputCarrier,
): boolean {
  const layoutInputKeys = Object.keys(workflow.inputs ?? {}).filter((k) => k.startsWith('Layout#'));
  let hasUpdates = false;

  for (const key of layoutInputKeys) {
    const [, componentName, encodedToken] = key.split('#');
    const token = decodeURIComponent(encodedToken);
    const override = tokenOverrides?.get(token) ?? DEFAULT_LAYOUT_INPUT_TOKENS.get(token);
    if (!override && token !== SELECTOR_INPUT_TOKEN) {
      console.warn(
        `Unsupported layout workflow input token '${token}' in '${key}' - input is unwired.`,
      );
      continue;
    }

    let elementId = context.idsMapping[componentName];
    if (!elementId && findElement(draft, componentName)) {
      elementId = componentName;
      context.idsMapping[componentName] = componentName;
    }
    if (!elementId) {
      console.warn(
        `Layout workflow input '${key}' references non existing component '${componentName}' - input is unwired.`,
      );
      continue;
    }

    const { name, eventType } =
      override ?? resolveSelectorInput(findElement(draft, elementId), elementDefinitions);

    const connection = createConnection(
      context.pageId,
      getUiRuntimeElement(elementId),
      getWorkflowRuntimeElement(workflow.id),
      eventType,
    );

    processWorkflowInputs(workflow, connection, context.idsMapping, new Map([[token, name]]), key);

    draft.connections.push(connection);
    hasUpdates = true;

    if (resolvedLayoutInputs) {
      resolvedLayoutInputs[key] = { inputName: name, connection };
    }
  }

  return hasUpdates;
}

function resolveSelectorInput(
  element: WritableDraft<CanvasElement> | undefined,
  elementDefinitions: ElementDefinitionsService,
): ResolvedLayoutInput {
  return {
    name: getWorkflowInputName(element as CanvasElement),
    eventType: elementDefinitions.getDefaultOutputEventType(element?.type) ?? 'change',
  };
}

export function resolveSourceWorkflowContext<T extends CanvasElementType>(
  context: ElementContext<T> & { workflowContext?: WorkflowContext },
): WorkflowContext | undefined {
  return context.workflowContext ?? context.componentWorkflows[context.element.id];
}

export function markWorkflowForceUpdateIfWired(
  workflowsToFinalize: AiWorkflowFinalizationEntry[] | undefined,
  workflow: Workflow,
  hasUpdates: boolean,
): void {
  if (!hasUpdates) {
    return;
  }
  const entry = workflowsToFinalize?.find((e) => e.workflow === workflow);
  if (entry) {
    entry.forceUpdate = true;
  }
}

/**
 * Default layout-input resolution for an element carrying a source workflow, used by the wiring
 * pass for every element type that does not implement `ElementHandler.wireCrossComponentInputs`.
 */
export function wireDefaultLayoutInputs<T extends CanvasElementType>(
  draft: WritableDraft<EngineState>,
  context: ElementContext<T> & {
    workflowContext?: WorkflowContext;
    workflowsToFinalize?: AiWorkflowFinalizationEntry[];
  },
  elementDefinitions: ElementDefinitionsService,
): void {
  const workflowContext = resolveSourceWorkflowContext(context);
  if (!workflowContext) {
    return;
  }

  const resolvedLayoutInputs = workflowContext.resolvedLayoutInputs ?? {};
  const hasUpdates = wireLayoutWorkflowInputs(
    draft,
    context,
    workflowContext.workflow,
    elementDefinitions,
    undefined,
    resolvedLayoutInputs,
  );

  if (hasUpdates) {
    workflowContext.resolvedLayoutInputs = resolvedLayoutInputs;
  }

  markWorkflowForceUpdateIfWired(context.workflowsToFinalize, workflowContext.workflow, hasUpdates);
}

/**
 * Resolves cross-component `Layout#…` inputs left on this element's action workflows (row/bulk
 * actions, button clicks, …) after `process()`
 */
export function wireActionWorkflowLayoutInputs<T extends CanvasElementType>(
  draft: WritableDraft<EngineState>,
  context: ElementContext<T> & { workflowsToFinalize?: AiWorkflowFinalizationEntry[] },
  elementDefinitions: ElementDefinitionsService,
): void {
  for (const entry of context.workflowsToFinalize ?? []) {
    const hasUpdates = wireLayoutWorkflowInputs(draft, context, entry.workflow, elementDefinitions);
    if (hasUpdates) {
      entry.forceUpdate = true;
    }
  }
}
